import { ProjectStatus } from '@prisma/client'

import type { PROJECT_POSTS, ProjectPost } from './project-posts'

/**
 * The value on `GET /projects` that selects the projects where the axis asking
 * names nobody — each post's filter, and the customer's. Shared so the query
 * builder and the filter UI cannot drift apart; every other value is a
 * `User.id`.
 *
 * One sentinel for all of them, because it says the same thing about each.
 */
export const PROJECT_PERSON_NONE = 'none'

/**
 * The filter that narrows the list to the reader's own projects.
 *
 * Not one of the posts: it matches every one of them and the access given
 * beside them, which is what a person means by "my projects". The server reads
 * it against whoever is asking, so no id travels in the link.
 */
export const MINE_PARAM = 'mine'

/**
 * What narrows the internal project list, as both sides spell it.
 *
 * One entry per post, named by the post, plus the axes that are not posts. A
 * string throughout because it travels through a URL: a `User.id`, or
 * `PROJECT_PERSON_NONE` for the projects that axis names nobody for.
 */
export type ProjectListFilters = {
  [P in ProjectPost as (typeof PROJECT_POSTS)[P]['param']]?: string
} & {
  status?: string
  customer?: string
  search?: string
  [MINE_PARAM]?: string
}

/**
 * The projects someone is working on: not yet public, finished or archived.
 * The project list opens on these, and the dashboards count them.
 */
export const ACTIVE_PROJECT_STATUSES: readonly ProjectStatus[] = [
  ProjectStatus.DRAFT,
  ProjectStatus.IN_PLANNING,
  ProjectStatus.UNDER_CONSTRUCTION,
]

/**
 * The projects whose offer is finished: what they were quoted is not going to
 * change again.
 *
 * Deliberately not the complement of `ACTIVE_PROJECT_STATUSES`, which answers
 * a different question — who is working on something. A `PUBLIC` project is an
 * exposé offered to whoever finds it: nobody is working on it and its
 * positions can still change, so it belongs on this side and not that one.
 *
 * Read by the confirmations a catalogue change has to get: stamping a position
 * is mechanical and covers every row, but asking an administrator to weigh one
 * only makes sense where the answer could still matter — and a list that grows
 * with every finished project is a list nobody reads.
 */
export const SETTLED_PROJECT_STATUSES: readonly ProjectStatus[] = [
  ProjectStatus.DONE,
  ProjectStatus.ARCHIVED,
]
